import { type Organisation, type TriageCategory, signById, signOrganisation } from '@orgsim/shared';
import {
  type TaktischesZeichen,
  erzeugeTaktischesZeichen,
  organisationen,
} from '@taktische-zeichen/core';

export const CATEGORY_COLORS: Record<TriageCategory, string> = {
  I: '#e53935',
  II: '#fdd835',
  III: '#43a047',
  IV: '#1e88e5',
  V: '#212121',
};

// White text at small sizes fails WCAG AA contrast (4.5:1) on every category colour except
// the near-black V; dark text passes on all the others, including the red (I) that reads
// close to the threshold with white. Chosen per category by contrast, not assumed white.
export const CATEGORY_TEXT_COLORS: Record<TriageCategory, string> = {
  I: '#000000',
  II: '#000000',
  III: '#000000',
  IV: '#000000',
  V: '#ffffff',
};

// Shared HTML/attribute escape for text interpolated into SVG or divIcon markup.
export const escapeHtml = (s: string) =>
  s.replace(
    /[<>&"]/g,
    (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[ch] ?? ch,
  );

// Tactical signs follow the BBK recommendation "Taktische Zeichen im Bevölkerungsschutz"
// (2024). Base shapes, colours and task symbols come from @taktische-zeichen/core; signs the
// library cannot compose add their own elements in the library's coordinate system
// (stelle: circle r 21.5 in 45x45, vehicle body 75x45 plus wheels) and stroke width. All
// text is placed here, never by the library, so it uses the sign font below and its metrics.

type OrgId = 'feuerwehr' | 'hilfsorganisation' | 'fuehrung';
type Box = [number, number, number, number]; // minX, minY, width, height in sign units

type Graphic = {
  body: string;
  box: Box;
  anchor: [number, number]; // the map position of the sign, in sign units
};

// Loaded by the page from @fontsource/barlow-semi-condensed (600), bundled with the app.
export const SIGN_FONT = { family: 'Barlow Semi Condensed', weight: 600 } as const;
const FONT_STYLE = `font-family:'${SIGN_FONT.family}';font-weight:${SIGN_FONT.weight}`;
const CAP_HEIGHT = 0.7; // cap height per font size, measured

const orgColors = (id: OrgId) => {
  const org = organisationen.find((o) => o.id === id);
  return { background: org?.background ?? '#ffffff', text: org?.textColor ?? '#000000' };
};

let idCounter = 0;

function library(spec: TaktischesZeichen, anchor: [number, number]): Graphic {
  const svg = erzeugeTaktischesZeichen({ ...spec, skipFontRegistration: true }).toString();
  const box = (svg.match(/viewBox="([^"]+)"/)?.[1] ?? '0 0 45 45').split(' ').map(Number) as Box;
  // Several signs share one document; clip paths and symbols need ids unique to each sign.
  const prefix = `tz${++idCounter}_`;
  const body = svg
    .replace(/^<\?xml[^>]*>\s*/, '')
    .replace(/(id="|#)tz_/g, `$1${prefix}`)
    .replace('<svg ', `<svg x="${box[0]}" y="${box[1]}" width="${box[2]}" height="${box[3]}" `);
  return { body, box, anchor };
}

// Advance widths of the sign font in 1/1000 em, measured from the bundled font file; enough
// to size text into a box. Unlisted characters count as 0.5 em.
const UPPER = [
  554, 540, 533, 543, 511, 487, 537, 557, 241, 512, 549, 493, 627, 586, 544, 524, 521, 537, 513,
  516, 557, 540, 771, 538, 528, 480,
];
const LOWER = [
  478, 498, 477, 498, 484, 333, 490, 491, 235, 231, 478, 219, 747, 491, 490, 502, 502, 342, 446,
  328, 489, 460, 677, 466, 446, 412,
];
const DIGITS = [509, 313, 488, 478, 522, 478, 479, 440, 484, 480];
const OTHER: Record<string, number> = { ' ': 200, '-': 364, '/': 417, '.': 242, ',': 232 };

// Text width in em.
export function textWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    const c = ch.charCodeAt(0);
    width +=
      (c >= 65 && c <= 90
        ? UPPER[c - 65]
        : c >= 97 && c <= 122
          ? LOWER[c - 97]
          : c >= 48 && c <= 57
            ? DIGITS[c - 48]
            : OTHER[ch]) ?? 500;
  }
  return width / 1000;
}

// Largest text (up to maxSize) whose width and cap height fit the box.
function fitText(
  text: string,
  [x0, y0, x1, y1]: [number, number, number, number],
  opts: { align: 'start' | 'middle' | 'end'; fill: string; maxSize?: number; halo?: string },
): string {
  // Rounded down so the written size never exceeds the box.
  const size =
    Math.floor(
      Math.min(opts.maxSize ?? 30, (x1 - x0) / textWidth(text), (y1 - y0) / CAP_HEIGHT) * 100,
    ) / 100;
  const x = opts.align === 'start' ? x0 : opts.align === 'end' ? x1 : (x0 + x1) / 2;
  const y = y1 - (y1 - y0 - size * CAP_HEIGHT) / 2;
  const halo = opts.halo
    ? ` stroke="${opts.halo}" stroke-width="${size / 5}" stroke-linejoin="round" paint-order="stroke"`
    : ' stroke="none"';
  return `<text x="${round(x)}" y="${round(y)}" font-size="${round(size)}" text-anchor="${opts.align}" fill="${opts.fill}"${halo} style="${FONT_STYLE}">${escapeHtml(text)}</text>`;
}

const round = (n: number) => Math.round(n * 100) / 100;

const overlay = (content: string, strokeWidth = 1.5) =>
  `<g fill="none" stroke="#000000" stroke-width="${strokeWidth}" stroke-linejoin="round">${content}</g>`;

function withOverlay(graphic: Graphic, content: string): Graphic {
  return { ...graphic, body: graphic.body + content };
}

function padded(
  graphic: Graphic,
  {
    left = 0,
    top = 0,
    right = 0,
    bottom = 0,
  }: Partial<Record<'left' | 'top' | 'right' | 'bottom', number>>,
): Graphic {
  const [minX, minY, w, h] = graphic.box;
  return { ...graphic, box: [minX - left, minY - top, w + left + right, h + top + bottom] };
}

// Short text left of the circle's upper edge, e.g. the unit type or the capacity.
function withCornerText(graphic: Graphic, text: string, right: number, baseline: number): Graphic {
  const size = 13;
  const width = textWidth(text) * size;
  const [minX, minY, w, h] = graphic.box;
  const left = round(Math.min(minX, right - width - 3));
  return {
    ...graphic,
    body: `${graphic.body}<text x="${right}" y="${baseline}" font-size="${size}" text-anchor="end" fill="#000000" stroke="none" style="${FONT_STYLE}">${escapeHtml(text)}</text>`,
    box: [left, minY, w + minX - left, h],
  };
}

const VEHICLE_CENTER: [number, number] = [37.5, 22.5];
const STELLE_CENTER: [number, number] = [22.5, 22.5];

// Upper left field of the medical cross on a Kfz: from the body's left stroke to the cross's
// vertical stroke (x 1 and 37.5, both 2 wide), 2 units clear of each.
export const VEHICLE_TYPE_FIELD: [number, number, number, number] = [4, 8, 34.5, 20];

// Kfz with the medical cross; the type sits in the upper left field of the cross.
function rescueVehicle(type: string, org: OrgId): Graphic {
  return withOverlay(
    library(
      {
        grundzeichen: 'kraftfahrzeug-landgebunden',
        organisation: org,
        fachaufgabe: 'rettungswesen',
      },
      VEHICLE_CENTER,
    ),
    fitText(type, VEHICLE_TYPE_FIELD, { align: 'start', fill: orgColors(org).text, maxSize: 16 }),
  );
}

// The library's helicopter is the older rotor pictogram and takes no task or type, so the
// current sign (closed dome with the medical cross, a small bow tie centred below) is drawn here.
function rescueHelicopter(org: OrgId): Graphic {
  const { background, text } = orgColors(org);
  const body = `<g fill="${background}" stroke="#000000" stroke-width="2" stroke-linejoin="round">
    <path d="M25.5,43 L37.5,48 L25.5,53 Z M49.5,43 L37.5,48 L49.5,53 Z" fill="#000000"/>
    <path d="M1.5,40 A36,36 0 0 1 73.5,40 Z"/>
    <path d="M37.5,4 V40 M5.25,24 H69.75" fill="none"/>
  </g>${fitText('RTH', [7, 27, 34.5, 37.5], { align: 'start', fill: text, maxSize: 16 })}`;
  return { body, box: [0, 0, 75, 55], anchor: [37.5, 29] };
}

// Person with the leader mark; two stars above it for the district-level leaders.
function leader(text: string, stars: boolean): Graphic {
  return withOverlay(
    library(
      {
        grundzeichen: 'person',
        organisation: 'fuehrung',
        funktion: 'fuehrungskraft',
        verwaltungsstufe: stars ? 'kreis' : undefined,
      },
      STELLE_CENTER,
    ),
    // Kept 3 units inside the diamond's edges at the text's top and bottom.
    fitText(text, [11, 17, 34, 28], { align: 'middle', fill: '#000000' }),
  );
}

const stelle = (spec: Omit<TaktischesZeichen, 'grundzeichen'>) =>
  library({ grundzeichen: 'stelle', ...spec }, STELLE_CENTER);

function casualtyCollection(): Graphic {
  return stelle({
    organisation: 'hilfsorganisation',
    fachaufgabe: 'rettungswesen',
    symbol: 'sichten',
  });
}

const RESCUE_TYPES: Record<string, string> = {
  rtw: 'RTW',
  ktw: 'KTW',
  nktw: 'N-KTW',
  nef: 'NEF',
  naw: 'NAW',
  itw: 'ITW',
  grtw: 'GRTW',
  gwsan: 'GW-San',
};

function graphic(signType: string, org: Organisation | undefined, label: string): Graphic {
  const rescueType = RESCUE_TYPES[signType];
  if (rescueType) return rescueVehicle(rescueType, org ?? 'hilfsorganisation');
  switch (signType) {
    case 'elw': {
      // Leadership band following the concave top edge of the body (the library's fuehrung
      // task draws a straight-edged bar instead), the type below it.
      const elwOrg = org ?? 'feuerwehr';
      return withOverlay(
        library(
          { grundzeichen: 'kraftfahrzeug-landgebunden', organisation: elwOrg },
          VEHICLE_CENTER,
        ),
        `<path d="M1,1 Q37.5,10 74,1 V9 Q37.5,18 1,9 Z" fill="#000000" stroke="#000000" stroke-width="2" stroke-linejoin="round"/>${fitText('ELW 1', [10, 17, 65, 36], { align: 'middle', fill: orgColors(elwOrg).text })}`,
      );
    }
    case 'lf':
      return withOverlay(
        library(
          {
            grundzeichen: 'kraftfahrzeug-landgebunden',
            organisation: 'feuerwehr',
            fachaufgabe: 'brandbekaempfung',
          },
          VEHICLE_CENTER,
        ),
        fitText('LF 20', [4, 8, 44, 20], { align: 'start', fill: '#ffffff', maxSize: 16 }),
      );
    case 'rth':
      return rescueHelicopter(org ?? 'hilfsorganisation');
    case 'orgl':
      return leader('OrgL', true);
    case 'lna':
      return leader('LNA', true);
    case 'eal':
      return leader('EAL', false);
    case 'uhs':
      return withCornerText(
        stelle({ organisation: 'hilfsorganisation', fachaufgabe: 'rettungswesen' }),
        'UHS',
        2,
        10,
      );
    case 'commandPost':
      return withOverlay(
        library(
          {
            // The command post of D.1.4 has no flag pole; this is the same rectangle without it.
            grundzeichen: 'taktische-formation',
            organisation: 'fuehrung',
            fachaufgabe: 'fuehrung',
          },
          VEHICLE_CENTER,
        ),
        fitText('EL', [10, 13, 65, 37], { align: 'middle', fill: '#000000' }),
      );
    case 'staging':
      // Room symbol: the vehicle body without wheels.
      return withOverlay(
        stelle({ organisation: 'fuehrung' }),
        overlay('<path d="M9,31 V14 Q22.5,20 36,14 V31 Z"/>'),
      );
    case 'casualtyCollection':
    case 'casualtyStation':
      return casualtyCollection();
    case 'treatment':
      // Stationary (roof), staffed by a physician (bar across the lower arm), capacity 50.
      // The roof's ends and apex stick out of the library's box by half a stroke.
      return withCornerText(
        padded(
          library(
            {
              grundzeichen: 'ortsfeste-stelle',
              organisation: 'hilfsorganisation',
              fachaufgabe: 'aerztliche-versorgung',
            },
            [23, 25],
          ),
          { left: 1, top: 1.5, right: 1 },
        ),
        '50',
        -1,
        14,
      );
    case 'assembly':
      return stelle({ organisation: 'hilfsorganisation', symbol: 'sammeln' });
    case 'helipad':
      return withOverlay(
        stelle({ organisation: 'fuehrung' }),
        '<path d="M9,14 L22.5,22.5 L9,31 Z M36,14 L22.5,22.5 L36,31 Z" fill="#000000" stroke="#000000" stroke-width="1" stroke-linejoin="round"/>',
      );
    case 'vehicleHolding':
      // Medical cross with a small wheel (transport symbol) where the arms cross.
      return withOverlay(
        stelle({ organisation: 'hilfsorganisation', fachaufgabe: 'rettungswesen' }),
        overlay(
          '<circle cx="22.5" cy="22.5" r="7" fill="#ffffff"/><path d="M15.5,22.5 H29.5 M22.5,15.5 V29.5 M17.55,17.55 L27.45,27.45 M17.55,27.45 L27.45,17.55"/>',
        ),
      );
    case 'loadingZone':
      // Person (diamond) above an arrow: persons are loaded here.
      return withOverlay(
        stelle({ organisation: 'hilfsorganisation' }),
        overlay(
          '<path d="M22.5,8 L29.5,15 L22.5,22 L15.5,15 Z M11,31 H33 M28.5,26.5 L33,31 L28.5,35.5"/>',
        ),
      );
    case 'section': {
      // The section itself is the area the instructor draws; the sign is its black label.
      const text =
        label === signById('section').label ? 'EA' : /^EA\b/.test(label) ? label : `EA ${label}`;
      const width = round(textWidth(text) * 20 + 4);
      return {
        body: fitText(text, [2, 8, width - 2, 37], {
          align: 'middle',
          fill: '#000000',
          maxSize: 20,
          halo: '#ffffff',
        }),
        box: [0, 0, width, 45],
        anchor: [width / 2, 22.5],
      };
    }
    default:
      return stelle({ organisation: 'hilfsorganisation' });
  }
}

export type SignIcon = { svg: string; width: number; height: number; anchor: [number, number] };

const CAPTION_PX = 12;

// Renders a sign `size` px high (plus the call sign line below, if any). Width follows the
// sign's aspect ratio; `anchor` is the pixel that belongs on the sign's map position.
export function signIcon(
  signType: string,
  opts: { label?: string; size?: number; organisation?: Organisation } = {},
): SignIcon {
  const def = signById(signType);
  const label = opts.label ?? def.label;
  const size = opts.size ?? 48;
  const g = graphic(signType, signOrganisation(signType, opts.organisation), label);
  const [minX, minY, w, h] = g.box;
  const scale = size / h;
  // The call sign goes under the sign unless it only repeats the catalogue name.
  const caption = signType !== 'section' && label && label !== def.label ? label : undefined;
  const captionUnits = caption ? (CAPTION_PX + 4) / scale : 0;
  const captionSvg = caption
    ? `<text x="${round(minX + w / 2)}" y="${round(minY + h + (CAPTION_PX + 2) / scale)}" font-size="${round(CAPTION_PX / scale)}" text-anchor="middle" fill="#ffffff" stroke="#000000" stroke-width="${round(3 / scale)}" stroke-linejoin="round" paint-order="stroke" style="${FONT_STYLE}">${escapeHtml(caption)}</text>`
    : '';
  const width = round(w * scale);
  const height = round((h + captionUnits) * scale);
  // Black outlines vanish on the dark map; a thin white glow keeps them visible.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${minX} ${minY} ${round(w)} ${round(h + captionUnits)}" style="overflow:visible;filter:drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff)">${g.body}${captionSvg}</svg>`;
  return {
    svg,
    width,
    height,
    anchor: [round((g.anchor[0] - minX) * scale), round((g.anchor[1] - minY) * scale)],
  };
}

export function signSvg(
  signType: string,
  opts: { label?: string; size?: number; organisation?: Organisation } = {},
): string {
  return signIcon(signType, opts).svg;
}

export function patientSvg(number: number, category?: TriageCategory): string {
  const fill = category ? CATEGORY_COLORS[category] : '#9e9e9e';
  const text = category ? CATEGORY_TEXT_COLORS[category] : '#ffffff';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <circle cx="14" cy="14" r="12" fill="${fill}" stroke="#ffffff" stroke-width="2"/>
    <text x="14" y="18" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12" font-weight="700" fill="${text}">${number}</text>
  </svg>`;
}
